
import Ajv from 'ajv'
import { DataCleaningService } from './DataCleaningService';
import { DataCleaningTypeGuardService } from './DataCleaningTypeGuardService';
import { destructureWithOmit, recursiveConvertSnakeToCamelCase } from '@/utils';
import { Rule } from '../models/Rule';
import { RuleSchema } from '../models/RuleSchema';
import { IQueueExecutionRequest } from '@/shared/Executions/models/IQueueExecutionRequest';
import { CdnSchemaJsonParsingResult } from '@/shared/JsonEditorSchema/models/CdnSchemaJsonParsingResult';
import { SchemaJsonValidationService } from '@/shared/JsonEditorSchema/services/SchemaJsonValidationService';


export class DataCleaningJsonParsingResult extends CdnSchemaJsonParsingResult<Rule, DataCleaningJsonParsingInputType> {

    constructor(
        valid: boolean,
        parsedValue?: any,
        errors: Array<string> = []
    ) {
        super(valid, parsedValue, errors);
        if (valid && parsedValue) {
            // Attempt to extract rules based on input shape
            if ('dataCleaning' in parsedValue && Array.isArray(parsedValue.dataCleaning?.rules)) {
                this.parsedEntities = parsedValue.dataCleaning.rules;
                this.inputType = 'dataCleaning';
            }
            else if ('rules' in parsedValue && Array.isArray(parsedValue.rules)) {
                this.parsedEntities = parsedValue.rules;
                this.inputType = 'flat';
            } else if (Array.isArray(parsedValue)) {
                this.parsedEntities = parsedValue;
                this.inputType = 'array';
            }
        }
    }

    public withParsedRules(rules: Rule[]): this {
        this.parsedEntities = rules;
        return this;
    }
}

// Represents any json structure with a dataCleaning property and rules Array property
interface IDataCleaningRulesArrayStructure {
    dataCleaning: {
        rules: Rule[];
    }
}

// Represents a potential input json file structure with a rules Array property
interface IFlatRulesArrayStructure {
    rules: Rule[];
}

// Represents a potential input json file structure that is solely an array of rule
type IRulesArray = Rule[];


export type DataCleaningJsonParsingInputType = 'full' | 'dataCleaning' | 'flat' | 'array';

export class DataCleaningJsonParsingService extends SchemaJsonValidationService<Rule, DataCleaningJsonParsingInputType, RuleSchema> {

    static readonly injectableKey = 'DataCleaningJsonParsingService';

    get injectableKey() {
        return (this.constructor as typeof DataCleaningJsonParsingService).injectableKey;
    }

    private _fullRulesPayloadStructureSchemaValidator = new Ajv().compile<IQueueExecutionRequest>({
        type: 'object',
        properties: {
            fileId: {
                type: 'string'
            },
            name: {
                type: 'string'
            },
            dataCleaning: {
                type: 'object',
                properties: {
                    rules: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                type: { type: 'string' }
                            },
                            required: ['type'],
                            additionalProperties: true
                        }
                    },
                    output: {
                        type: 'object',
                        properties: {
                            fileName: { type: 'string' }
                        },
                        required: ['fileName'],
                        additionalProperties: true
                    }
                },
                required: ['rules'],
                additionalProperties: true
            }
        },
        required: ['dataCleaning', 'fileId'],
        additionalProperties: true
    });

    private _dataCleaningRulesArrayStructureSchemaValidator = new Ajv().compile<IDataCleaningRulesArrayStructure>({
        type: 'object',
        properties: {
            dataCleaning: {
                type: 'object',
                properties: {
                    rules: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                type: { type: 'string' }
                            },
                            required: ['type'],
                            additionalProperties: true
                        }
                    },
                    output: {
                        type: 'object',
                        properties: {
                            fileName: { type: 'string' }
                        },
                        required: ['fileName'],
                        additionalProperties: true
                    }
                },
                required: ['rules'],
                additionalProperties: true
            }
        },
        required: ['dataCleaning'],
        additionalProperties: true
    });

    private _flatRulesArrayStructureSchemaValidator = new Ajv().compile<IFlatRulesArrayStructure>({
        type: 'object',
        properties: {
            rules: {
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        type: { type: 'string' }
                    },
                    required: ['type'],
                    additionalProperties: true
                }
            }
        },
        required: ['rules'],
        additionalProperties: true
    });

    private _rulesArraySchemaValidator = new Ajv().compile<IRulesArray>({
        type: 'array',
        items: {
            type: 'object',
            properties: {
                type: { type: 'string' }
            },
            required: ['type'],
            additionalProperties: true
        }
    });

    //@ts-ignore
    public initializeValidators(defaultType?: string): void {
        //noop
    }

    // Attempts to parse, validate, and initialize rules array that can operate with the UI
    // Returns validation result indicating parse and schema validation success, optionally on success
    // with UI-initializd rules array.
    public tryParseEntitiesFromJson(value: string, ruleSchemas: Array<RuleSchema>): DataCleaningJsonParsingResult {
        if (ruleSchemas == null || ruleSchemas.length <= 0) throw new Error("ruleSchemas is required");

        var result = new DataCleaningJsonParsingResult(true);

        if (value == null) {
            result.valid = false;
            result.errors.push("Cannot parse JSON from null or empty input value.");
            return result;
        }
        try {
            result.parsedValue = JSON.parse(value);
            recursiveConvertSnakeToCamelCase(result.parsedValue)
            return this.tryParseRules(result, ruleSchemas);
        }
        catch (error: any) {
            result.valid = false;
            result.errors.push(error);
        }

        return result;

    }

    private tryParseRules(result: DataCleaningJsonParsingResult, ruleSchemas: Array<RuleSchema>): DataCleaningJsonParsingResult {

        if (result.parsedValue == null) throw new Error("Invalid rules.parseValue encountered trying to validateRulesStructure.");

        const parsed = result.parsedValue;
        let rules: Rule[] | undefined;

        // reset errors from any prior validation
        this._fullRulesPayloadStructureSchemaValidator.errors = null;
        this._dataCleaningRulesArrayStructureSchemaValidator.errors = null;
        this._flatRulesArrayStructureSchemaValidator.errors = null;
        this._rulesArraySchemaValidator.errors = null;

        if (this._fullRulesPayloadStructureSchemaValidator(parsed)) {
            rules = parsed.dataCleaning.rules;
        } else if (this._dataCleaningRulesArrayStructureSchemaValidator(parsed)) {
            rules = parsed.dataCleaning.rules;
        } else if (this._flatRulesArrayStructureSchemaValidator(parsed)) {
            rules = parsed.rules;
        } else if (this._rulesArraySchemaValidator(parsed)) {
            rules = parsed;
        } else {
            const errors = [
                ...(this._fullRulesPayloadStructureSchemaValidator.errors || []),
                ...(this._flatRulesArrayStructureSchemaValidator.errors || []),
                ...(this._rulesArraySchemaValidator.errors || [])
            ]
                .map((e: any) => `${e.instancePath} ${e.message}`)
                .slice(0, 5);

            return new DataCleaningJsonParsingResult(false, undefined, errors);
        }

        try {
            const initialized = rules.map(x => this.initializeEntityWithConfiguration(x, ruleSchemas));
            return new DataCleaningJsonParsingResult(true, parsed).withParsedRules(initialized);
        } catch (err: any) {
            return new DataCleaningJsonParsingResult(false, parsed, [`${err.message}`]);
        }

    }

    // For lack of a better term, invert the flattening of a rule and instead populate its configuration
    // from the rule itself when loading rules into the UI
    public initializeEntityWithConfiguration(rule: Rule, ruleSchemas: Array<RuleSchema>): Rule {
        if (rule == null) throw new Error("rule is required");
        if (!rule.type) throw new Error("rule.type is required")
        return this.initializeEntity(rule, ruleSchemas, 1);
    }

    public initializeEntity(rule: Rule, ruleSchemas: Array<RuleSchema>, configurationLevel: number): Rule {
        let initializedRule = rule;
        //Match the incoming type on case-sensitive known rules
        const ruleSchema = DataCleaningService.tryGetRuleSchemaByType(rule.type, ruleSchemas);
        if (ruleSchema != null) {
            initializedRule = DataCleaningService.createRuleFromRuleSchema(ruleSchema);
            if (rule.description != null) initializedRule.description = rule.description;
            initializedRule._type = rule.type;
            initializedRule.configuration = destructureWithOmit(rule, []);
            initializedRule.configuration._type = rule.type;

            // Special-case for rule comprised of rules (E.g. Data Grouping) or RuleSchema with payload_template that may have groups 
            const ruleRequiresRecursiveInitialization = this.ruleRequiresRecursiveInitialization(rule);
            //TODO: evaluate payload_template itself for groups instead of just disabling
            const ruleSchemaRequiresRecursiveInitialization = this.ruleSchemaRequiresRecursiveInitialization(ruleSchema);

            if (configurationLevel > 1 &&
                (ruleRequiresRecursiveInitialization || ruleSchemaRequiresRecursiveInitialization)) {
                throw new Error("Non top-level rules that contain rules not supported.");
            }
            else if (ruleRequiresRecursiveInitialization) {
                const initializedSubRules = new Array<Rule>();
                (initializedRule.configuration?.rules || []).forEach((r: Rule) => {
                    initializedSubRules.push(this.initializeEntity(r, ruleSchemas, configurationLevel + 1))
                });
                initializedRule.configuration.rules = initializedSubRules;
            }
        }
        return initializedRule;
    }

    private ruleRequiresRecursiveInitialization(rule: Rule): boolean {
        return DataCleaningTypeGuardService.supportsChildRules(rule);
    }

    private ruleSchemaRequiresRecursiveInitialization(ruleSchema: RuleSchema): boolean {
        return DataCleaningTypeGuardService.supportsChildRules(ruleSchema);
    }



}